import { useState, useEffect, useMemo } from "react";
import SuccessPopup from "src/business-types/laundry/modules/home/Done/SuccessPopup";
import EmailModal from "src/business-types/laundry/modules/home/Done/EmailModal";
import { useOrder } from "src/business-types/laundry/modules/home/Done/hooks/useOrder";
import { useCustomer } from "src/business-types/laundry/modules/home/Done/hooks/useCustomer";
import { useInvoiceEmail } from "src/business-types/laundry/modules/home/Done/hooks/useInvoiceEmail";
import { usePrintData } from "src/business-types/laundry/modules/home/Done/hooks/usePrintData";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { useUser } from "src/shared/context/UserContext";
import { CustomerData } from "src/shared/services/interface";

interface DoneProps {
  orderid?: string;
  onFinish: () => void;
  customer?: CustomerData | any;
}

const Done: React.FC<DoneProps> = ({ orderid, onFinish, customer }) => {
  const { order } = useOrder(orderid);

  const orderCustomerId = order?.customerId || order?.orders?.[0]?.customerId || order?.customerDetails?._id?.$oid;

  const outstandingBalance = useMemo(() => {
    return order?.totalOutstandingBalance ?? 0;
  }, [order?.totalOutstandingBalance]);
  const { clientId, updateEmail } = useCustomer(orderCustomerId);
  const { user } = useUser();
  const { sendInvoiceEmail } = useInvoiceEmail();

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(true);

  const resolvedEmail = order?.CustomerEmail || order?.customerDetails?.emails?.[0]?.email || order?.orders?.[0]?.customerDetails?.emails?.[0]?.email || "";

  const [email, setEmail] = useState(resolvedEmail);
  const [updatedEmail, setUpdatedEmail] = useState(resolvedEmail);
  const [emailError, setEmailError] = useState("");

  const printData = usePrintData(order, outstandingBalance, clientId, user?.orgArabicName || "", user?.businessLocation || "", customer);

  useEffect(() => {
    const latestEmail = order?.CustomerEmail || order?.customerDetails?.emails?.[0]?.email || order?.orders?.[0]?.customerDetails?.emails?.[0]?.email || "";

    setEmail(latestEmail);
    setUpdatedEmail(latestEmail);
  }, [order]);

  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSendMailClick = () => {
    const currentEmail = updatedEmail || email || order?.CustomerEmail || order?.customerDetails?.emails?.[0]?.email || order?.orders?.[0]?.customerDetails?.emails?.[0]?.email;

    if (!currentEmail?.trim()) {
      setShowEmailModal(true);
      return;
    }

    sendInvoiceEmail(currentEmail, order)
      .then(() => {
        setTimeout(() => {
          onFinish();
        }, 2000);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleEmailUpdate = async () => {
    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError("Enter a valid email address");
      return;
    }

    try {
      const customerId = order?.customerId || order?.orders?.[0]?.customerId || order?.customerDetails?._id?.$oid;

      if (!customerId) {
        notifyError("Customer ID not found");
        return;
      }

      const payload = {
        emails: [
          {
            email: email.trim(),
            isPrimary: true,
          },
        ],
      };

      await updateEmail(customerId, payload);

      notifySuccess("Email updated successfully!");

      setUpdatedEmail(email.trim());
      setEmail(email.trim());

      setShowEmailModal(false);
      setEmailError("");

      await sendInvoiceEmail(email.trim(), order);

      setTimeout(() => {
        setShowSuccess(false);
        onFinish();
      }, 3000);
    } catch (error: any) {
      console.error(error);

      const backendMsg = error?.response?.data?.message;

      notifyError(backendMsg || "Failed to update email.");

      if (backendMsg === "Email already exists") {
        setEmailError("Email already exists.");
        return;
      }

      setEmailError("Failed to update email. Try again.");
    }
  };


  return (
    <div>
      {showSuccess && (
        <SuccessPopup
          onSendMail={handleSendMailClick}
          onSendMessage={() => ""}
          onDone={() => {
            setShowSuccess(false);
            onFinish();
          }}
          printData={printData}
          osb={outstandingBalance}
          disabled={showEmailModal}
          order={order}
        />
      )}

      {showEmailModal && (
        <EmailModal
          email={email}
          emailError={emailError}
          onChange={(e) => {
            setEmail(e);
            setEmailError("");
          }}
          onCancel={() => {
            setShowEmailModal(false);
            setEmailError("");
          }}
          onUpdate={handleEmailUpdate}
        />
      )}
    </div>
  );
};

export default Done;
